import React, { useState, useEffect } from 'react';
import { Button, Modal } from '@attentive-platform/stem-ui';
import { fetchApiData } from '../../Utils/ApiCalls';
import { Alert } from '../Alert';
import SOPTradeCard from './SOPTradeCard';
import SOPVersionHistory from './SOPVersionHistory';
import SOPVersionChanges from './SOPVersionChanges';
import SOPNewState from './SOPNewState';
import SOPAddBar from './SOPAddBar';
import SOPAddAllTradesModal from './SOPAddAllTradesModal';
import ScopeLinkage from './ScopeLinkage';

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export default function SOPManagement() {
  const [email, setEmail] = useState('');
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trades, setTrades] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [history, setHistory] = useState([]); // Version history from API
  const [showRemove, setShowRemove] = useState({ show: false, tradeIdx: null });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addTradeValue, setAddTradeValue] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [showLegacySetup, setShowLegacySetup] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [showAddAllConfirm, setShowAddAllConfirm] = useState(false);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Helper: get visible and addable trades
  const getVisibleTrades = () =>
    trades.filter(
      t => (t.assigned_included_scopes && t.assigned_included_scopes.length > 0) ||
           (t.assigned_additional_scopes && t.assigned_additional_scopes.length > 0) ||
           t._added === true
    );
  const getAddableTrades = () =>
    trades.filter(
      t => !((t.assigned_included_scopes && t.assigned_included_scopes.length > 0) ||
             (t.assigned_additional_scopes && t.assigned_additional_scopes.length > 0) ||
             t._added === true)
    );

  const handleFindCustomer = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    setShowPanel(false);
    setShowLegacySetup(false);
    setTrades([]);
    setHistory([]);
    setAddTradeValue('');
    try {
      const { data, error } = await fetchApiData({
        endpoint: '/api/dashboard/company_scopes/',
        apiRequest: 'GET',
        params: { user_email: email }
      });
      let errorMsg = error;
      if (error && typeof error === 'object') {
        errorMsg = error.message || JSON.stringify(error);
      }
      if (errorMsg || !data) {
        setError(errorMsg || 'No customer or SOP data found for this email. Please check the email and try again.');
        setLoading(false);
        setShowPanel(false);
        return;
      }
      const tradesFromApi = Array.isArray(data.data) ? deepClone(data.data) : [];
      // Empty legacy trades is valid — company can still use Rosetta scope linkage.
      if (!data.company_id && !data.company_name && tradesFromApi.length === 0) {
        setError('No customer or SOP data found for this email. Please check the email and try again.');
        setLoading(false);
        setShowPanel(false);
        return;
      }
      setCompanyName(data.company_name || '');
      setCompanyId(data.company_id || '');
      // Store the original order of included and additional scopes for each trade
      const augmentedTrades = tradesFromApi.map(t => ({
        ...t,
        _added: false,
        included_scopes_order: [
          ...(t.assigned_included_scopes || []),
          ...(t.remaining_included_scopes || [])
        ].map(i => i.id),
        additional_scopes_order: [
          ...(t.assigned_additional_scopes || []),
          ...(t.remaining_additional_scopes || [])
        ].map(i => i.id)
      }));
      setTrades(augmentedTrades);
      // Parse versions for history
      if (Array.isArray(data.versions)) {
        setHistory(
          data.versions.map(v => ({
            version: v.version,
            date: v.created_at ? v.created_at.replace('T', ' ').slice(0, 16) : '',
            author: v.author || '',
            changes: JSON.stringify(v.changes, null, 2),
            new_state: v.new_state ? JSON.stringify(v.new_state, null, 2) : null
          }))
        );
        setExpandedHistory({ 0: true });
      } else {
        setHistory([]);
        setExpandedHistory({});
      }
      setShowPanel(true);
      setSuccess(
        tradesFromApi.length
          ? 'Customer and SOP loaded successfully.'
          : 'Customer loaded — no legacy SOP yet. Link a scope record or set up legacy below.'
      );
    } catch (err) {
      let errorMsg = err;
      if (err && typeof err === 'object') {
        errorMsg = err.message || JSON.stringify(err);
      }
      setError(errorMsg || 'An error occurred while fetching data. Please check the email and try again.');
      setShowPanel(false);
    }
    setLoading(false);
  };

  const handleCollapse = (idx) => {
    setCollapsed((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleRemoveTrade = (idx) => {
    setShowRemove({ show: true, tradeIdx: idx });
  };

  // Select/Deselect all items in a single trade
  const handleSelectAllInTrade = (tradeIdx, selectAll) => {
    setTrades(prev => prev.map((trade, i) => {
      if (i !== tradeIdx) return trade;

      const allIncluded = [
        ...(trade.assigned_included_scopes || []),
        ...(trade.remaining_included_scopes || [])
      ];
      const allAdditional = [
        ...(trade.assigned_additional_scopes || []),
        ...(trade.remaining_additional_scopes || [])
      ];

      if (selectAll) {
        const updated = {
          ...trade,
          assigned_included_scopes: allIncluded,
          remaining_included_scopes: [],
          assigned_additional_scopes: allAdditional,
          remaining_additional_scopes: []
        };
        const hadScopes = (trade.assigned_included_scopes || []).length > 0 || (trade.assigned_additional_scopes || []).length > 0;
        const hasScopesNow = (updated.assigned_included_scopes || []).length > 0 || (updated.assigned_additional_scopes || []).length > 0;
        if (!hadScopes && hasScopesNow) {
          updated.notes = trade._originalNotes || trade.notes || '';
          if (updated._originalNotes) delete updated._originalNotes;
        }
        return updated;
      }

      // deselect all
      const updated = {
        ...trade,
        _added: true,
        remaining_included_scopes: allIncluded,
        assigned_included_scopes: [],
        remaining_additional_scopes: allAdditional,
        assigned_additional_scopes: []
      };
      if (trade.notes) {
        updated._originalNotes = trade.notes;
      }
      updated.notes = '';
      return updated;
    }));
  };

  // Global: Add all trades and select all items
  const openAddAllConfirm = () => setShowAddAllConfirm(true);
  const closeAddAllConfirm = () => setShowAddAllConfirm(false);
  const confirmAddAllTradesAndItems = () => {
    setTrades(prev => prev.map(trade => {
      const allIncluded = [
        ...(trade.assigned_included_scopes || []),
        ...(trade.remaining_included_scopes || [])
      ];
      const allAdditional = [
        ...(trade.assigned_additional_scopes || []),
        ...(trade.remaining_additional_scopes || [])
      ];
      const updated = {
        ...trade,
        _added: true,
        assigned_included_scopes: allIncluded,
        remaining_included_scopes: [],
        assigned_additional_scopes: allAdditional,
        remaining_additional_scopes: []
      };
      const hadScopes = (trade.assigned_included_scopes || []).length > 0 || (trade.assigned_additional_scopes || []).length > 0;
      const hasScopesNow = (updated.assigned_included_scopes || []).length > 0 || (updated.assigned_additional_scopes || []).length > 0;
      if (!hadScopes && hasScopesNow) {
        updated.notes = trade._originalNotes || trade.notes || '';
        if (updated._originalNotes) delete updated._originalNotes;
      }
      return updated;
    }));
    setShowAddAllConfirm(false);
  };

  const confirmRemoveTrade = () => {
    const idx = showRemove.tradeIdx;
    setTrades((prev) => prev.map((t, i) => {
      if (i === idx) {
          return {
            ...t,
            assigned_included_scopes: [],
            assigned_additional_scopes: [],
            notes: '',
            _added: false // This will move it back to the dropdown
          };
      }
      return t;
    }));
    setShowRemove({ show: false, tradeIdx: null });
  };

  const handleCheckbox = (tradeIdx, group, itemId) => {
    setTrades((prev) => prev.map((trade, i) => {
      if (i !== tradeIdx) return trade;

      // Create a new trade object with updated scopes
      const assignedKey = group.startsWith('included') ? 'assigned_included_scopes' : 'assigned_additional_scopes';
      const remainingKey = group.startsWith('included') ? 'remaining_included_scopes' : 'remaining_additional_scopes';
      const isAssigned = trade[assignedKey].some((item) => item.id === itemId);
      
      let updatedTrade;
      if (isAssigned) {
        const item = trade[assignedKey].find((item) => item.id === itemId);
        updatedTrade = {
          ...trade,
          [assignedKey]: trade[assignedKey].filter((item) => item.id !== itemId),
          [remainingKey]: [...trade[remainingKey], item],
        };
      } else {
        const item = trade[remainingKey].find((item) => item.id === itemId);
        updatedTrade = {
          ...trade,
          [assignedKey]: [...trade[assignedKey], item],
          [remainingKey]: trade[remainingKey].filter((item) => item.id !== itemId),
        };
      }

      const hadScopes = (trade.assigned_included_scopes || []).length > 0 || (trade.assigned_additional_scopes || []).length > 0;
      const hasScopesNow = (updatedTrade.assigned_included_scopes || []).length > 0 || (updatedTrade.assigned_additional_scopes || []).length > 0;

      // Transitioning from having scopes to no scopes
      if (hadScopes && !hasScopesNow) {
        if (trade.notes) {
          updatedTrade._originalNotes = trade.notes;
        }
        updatedTrade.notes = '';
      }
      // Transitioning from no scopes to having scopes
      else if (!hadScopes && hasScopesNow) {
        updatedTrade.notes = trade._originalNotes || '';
        if (updatedTrade._originalNotes) {
          delete updatedTrade._originalNotes;
        }
      }

      return updatedTrade;
    }));
  };

  const handleNotes = (tradeIdx, value) => {
    setTrades((prev) => prev.map((trade, i) =>
      i === tradeIdx ? { ...trade, notes: value } : trade
    ));
  };

  // Add trade from dropdown
  const handleAddTrade = () => {
    if (!addTradeValue) return;
    setTrades(prev => {
      // Find the trade to add
      const idx = prev.findIndex(trade => trade.trade_id === addTradeValue);
      if (idx === -1) return prev;
      const tradeToAdd = prev[idx];
      // If already visible, do nothing
      if ((tradeToAdd.assigned_included_scopes && tradeToAdd.assigned_included_scopes.length > 0) ||
          (tradeToAdd.assigned_additional_scopes && tradeToAdd.assigned_additional_scopes.length > 0) ||
          tradeToAdd._added === true) {
        setAddTradeValue('');
        return prev;
      }
      // Set _added to true and move to end
      const updatedTrade = { ...tradeToAdd, _added: true };
      const filtered = prev.filter((_, i) => i !== idx);
      return [...filtered, updatedTrade];
    });
    setAddTradeValue('');
  };

  // Save logic: post data and render response
  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');
    // setSaveResponse(null); // This is no longer needed

    const scope_ids = trades.flatMap(trade => [
      ...(trade.assigned_included_scopes || []).map(scope => scope.id),
      ...(trade.assigned_additional_scopes || []).map(scope => scope.id)
    ]);
    const notes_data = {};
    trades.forEach(trade => {
      if (trade.notes) {
        notes_data[trade.trade_id] = trade.notes;
      }
    });

    const payload = {
      user_email: email,
      scope_ids,
      notes_data,
    };

    try {
      const { data, error } = await fetchApiData({
        endpoint: '/api/dashboard/company_scopes/',
        apiRequest: 'POST',
        data: payload
      });

      let errorMsg = error;
      if (error && typeof error === 'object') {
        errorMsg = error.message || JSON.stringify(error);
      }

      if (errorMsg) {
        setError(errorMsg);
      } else {
        setSuccess('SOP saved successfully!');
        // Update version history from response if available
        if (data && Array.isArray(data.versions)) {
          setHistory(
            data.versions.map(v => ({
              version: v.version,
              date: v.created_at ? v.created_at.replace('T', ' ').slice(0, 16) : '',
              author: v.author || '',
              changes: JSON.stringify(v.changes, null, 2),
              new_state: v.new_state ? JSON.stringify(v.new_state, null, 2) : null
            }))
          );
          setExpandedHistory({ 0: true });
        } else if (data && data.version) {
          setHistory(prev => [
            {
              version: data.version.version,
              date: data.version.created_at ? data.version.created_at.replace('T', ' ').slice(0, 16) : '',
              author: data.version.author || '',
              changes: JSON.stringify(data.version.changes, null, 2),
              new_state: data.version.new_state ? JSON.stringify(data.version.new_state, null, 2) : null
            },
            ...prev
          ]);
          setExpandedHistory(prev => ({ 0: true }));
        }
      }
    } catch (err) {
      let errorMsg = err;
      if (err && typeof err === 'object') {
        errorMsg = err.message || JSON.stringify(err);
      }
      setError(errorMsg || 'An error occurred while saving the SOP.');
    }

    setIsSaving(false);
  };

  const toggleHistoryCollapse = (index) => {
    setExpandedHistory(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Move back button logic to a handler
  const handleBack = () => {
    setShowPanel(false);
    setShowLegacySetup(false);
    setSuccess('');
    setError('');
  };

  return (
    <>
      <form onSubmit={handleFindCustomer} className={showPanel ? 'sop-main-container' : 'sop-lookup-container main-container'}>
        <div className='sop-alert-sticky'>
          <Alert errorMessage={error} successMessage={success} />
        </div>
        {/* The entire saveResponse block is removed */}
        {!showPanel && (
          <>
            <div className='form-group mb-3'>
              <label htmlFor='customer-email' className='sop-customer-label'>Find Customer by User Email</label>
              <input
                type='email'
                id='customer-email'
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className='form-control sop-customer-input'
                placeholder='Enter Company Email'
                aria-label='Company Email'
              />
            </div>
            <Button color='primary' size='large' variant='contained' type='submit' loading={loading} className='w-100 fw-semibold'>
              Find Customer & Manage SOP
            </Button>
          </>
        )}
        {showPanel && (
          <div>
            <div className='sop-back-btn-row'>
              <Button color='primary' size='medium' variant='outlined' onClick={handleBack}>
                ← Back
              </Button>
            </div>
            <h2 className='sop-heading'>
              SOP Management{companyName ? ` for ${companyName}` : ''}
            </h2>
            <ScopeLinkage
              company={companyName}
              companyId={companyId}
              email={email}
              hasLegacyTrades={getVisibleTrades().length > 0}
              onLegacySetup={() => setShowLegacySetup(true)}
            />
            {showLegacySetup && (
              <>
                {/* Trade Cards */}
                {getVisibleTrades().length === 0 && <div>No trades found.</div>}
                {getVisibleTrades().map((trade) => (
                  <SOPTradeCard
                    key={trade.trade_id}
                    trade={trade}
                    idx={trades.findIndex(t => t.trade_id === trade.trade_id)}
                    collapsed={collapsed}
                    handleCollapse={handleCollapse}
                    handleRemoveTrade={handleRemoveTrade}
                    handleCheckbox={handleCheckbox}
                    handleNotes={handleNotes}
                    handleSelectAllInTrade={handleSelectAllInTrade}
                  />
                ))}
                {/* Add Trade Section (moved to bottom) */}
                {getAddableTrades().length > 0 && (
                  <SOPAddBar
                    addTradeValue={addTradeValue}
                    setAddTradeValue={setAddTradeValue}
                    addableTrades={getAddableTrades()}
                    onAddTrade={handleAddTrade}
                    onOpenAddAllConfirm={openAddAllConfirm}
                  />
                )}

                <Button color='primary' size='large' variant='contained' className='w-100 my-4 fw-semibold' onClick={handleSave} loading={isSaving}>
                  Save Changes
                </Button>

                {/* Version history */}
                <SOPVersionHistory
                  history={history}
                  expandedHistory={expandedHistory}
                  toggleHistoryCollapse={toggleHistoryCollapse}
                  renderChanges={changesStr => <SOPVersionChanges changesStr={changesStr} />}
                  renderNewState={newStateStr => <SOPNewState newStateStr={newStateStr} />}
                />
              </>
            )}
          </div>
        )}
      </form>
      {/* Move Modal outside the form */}
      <Modal
        show={showRemove.show}
        heading={<span className='pl-4'>Remove Trade</span>}
        onClose={() => setShowRemove({ show: false, tradeIdx: null })}
        content={
          <div className='flex flex-col gap-2 m-4 w-[300px]'>
            <div>Are you sure you want to remove this trade?</div>
            <div className='flex justify-end gap-2 mt-4'>
              <Button size='medium' variant='outlined' onClick={() => setShowRemove({ show: false, tradeIdx: null })}>
                Cancel
              </Button>
              <Button color='error' size='medium' onClick={confirmRemoveTrade}>
                Remove
              </Button>
            </div>
          </div>
        }
      />
      <SOPAddAllTradesModal
        show={showAddAllConfirm}
        onClose={closeAddAllConfirm}
        onConfirm={confirmAddAllTradesAndItems}
      />
    </>
  );
} 